/**
 * Global Tools Component
 * This component provides a set of global tools for the application.
 * It includes utility functions and shared UI elements.
 * @module components/global/GlobalTools
 *
 * @author Mario Simbron Gonzalez <mariosimbrongonzalez@example.com>
 * @date 03/12/2025 09:10 AM
 * @version 1.0.0
 */

/**
 * Escapes double quotes and newline characters in a string.
 * @param {string} str - The input string to be escaped.
 * @returns {string} - The escaped string.
 */
export function escapeQuotes(str) {
  if (str == null || typeof str !== "string") {
    return null;
  }
  // Replace double quotes with escaped double quotes
  return str.replace(/(?:\r\n|\r|\n|")/g, (match) => {
    // Check if the match is a double quote
    if (match === '"') {
      return '\\"';
    }
    // Otherwise, it's a newline character
    else {
      return "\\n";
    }
  });
}

/**
 * Normaliza la dirección visible de un customer.
 * El backend usa `shipping_address` como Ship-To para Acumatica y para el correo de
 * confirmación, así que la UI tiene que mostrar y editar esa misma dirección.
 * `billing_address` queda solo como fallback para registros legacy sin shipping
 * (Authorize.NET sigue usando billing para el AVS, eso no cambia).
 * @param {object} customer - Customer con las relaciones shipping_address / billing_address.
 * @returns {object} - El customer con `address` (la dirección de envío efectiva) y
 * `address_diverges` (true si billing y shipping apuntan a registros distintos).
 */
export function normalizeCustomerAddress(customer) {
  if (customer == null) {
    return customer;
  }
  const shipping_address = customer.shipping_address;
  const billing_address = customer.billing_address;
  return {
    ...customer,
    address: shipping_address || billing_address || null,
    address_diverges: Boolean(
      shipping_address &&
        billing_address &&
        shipping_address.id !== billing_address.id
    ),
  };
}

/**
 * Aplica normalizeCustomerAddress a una lista de customers.
 * @param {Array} customers - Lista devuelta por listUserCustomers / listClientCustomers.
 * @returns {Array} - Lista normalizada, o un arreglo vacío si no se recibió una lista.
 */
export function normalizeCustomerAddresses(customers) {
  if (!Array.isArray(customers)) {
    return [];
  }
  return customers.map((customer) => normalizeCustomerAddress(customer));
}

/**
 * Arma la dirección en una sola línea, omitiendo los campos vacíos.
 * Tolera address nulo: hay customers legacy sin shipping_address ni billing_address.
 * @param {object} address - Registro de dirección (Street, Street2, City, State, Zip, Country).
 * @returns {string} - La dirección en una línea, o cadena vacía si no hay dirección.
 */
export function formatAddressLine(address) {
  if (address == null) {
    return "";
  }
  const street = [address.Street, address.Street2]
    .filter((part) => part)
    .join(" ");
  const region = [address.City, address.State, address.Zip, address.Country]
    .filter((part) => part)
    .join(", ");
  return [street, region].filter((part) => part).join(", ");
}

/**
 * Turns the ENUM_ORDERINVOICE_SHIPPINGPREFERENCE enum value into readable text.
 * @param {string} shipping_preference - ShipComplete | ShipPartial
 * @returns {string} - "Ship Complete" | "Ship Partial", or an empty string when there is no value.
 */
export function getShippingPreferenceLabel(shipping_preference) {
  if (!shipping_preference) {
    return "";
  }
  return shipping_preference.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
}
